import type { Column, FilterFn, Row } from "@tanstack/react-table";
import { clsx, type ClassValue } from "clsx";
import { jwtVerify } from "jose";
import type { CSSProperties } from "react";
import { toast } from "sonner";
import { twMerge } from "tailwind-merge";

import { Temperature, TemperatureUnit, WastagePhase } from "@/types/meal-plan";
import { UserProfile } from "@/types/auth";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const generateUniqueColors = (count: number) => {
  const colors = [];
  for (let i = 0; i < count; i++) {
    colors.push(`hsl(${(i * 360) / count}, 70%, 50%)`);
  }
  return colors;
};

export function multiColumnFilterFn<TData>(
  row: Row<TData>,
  columnId: string,
  filterValue: string
): boolean {
  if (!filterValue) return true; // If no filter is applied, show all rows

  const searchTerm = filterValue.toLowerCase();

  // Extract the column value dynamically
  const columnValue = row.getValue(columnId);

  // Convert value to string for filtering
  const searchableContent = String(columnValue ?? "").toLowerCase();

  return searchableContent.includes(searchTerm);
}

export function statusFilterFn<TData>(
  row: Row<TData>,
  columnId: string,
  filterValue: string[]
): boolean {
  if (!filterValue.length) return true; // No filter applied, show all rows

  const status = row.getValue(columnId);

  if (typeof status !== "string") return false; // Ensure value is a string before filtering

  return filterValue.includes(status);
}

/**
 * Calculates the CSS styles needed to pin a column to the left or right of its container.
 *
 * @param column the column to calculate the styles for
 * @returns an object with the styles to apply to the column
 */
export const getPinningStyles = <T>(column: Column<T>): CSSProperties => {
  const isPinned = column.getIsPinned();
  return {
    left: isPinned === "left" ? `${column.getStart("left")}px` : undefined,
    right: isPinned === "right" ? `${column.getAfter("right")}px` : undefined,
    position: isPinned ? "sticky" : "relative",
    width: column.getSize(),
    zIndex: isPinned ? 1 : 0,
  };
};

export const dateFilterFn = <T, K extends keyof T>(
  columnId: K
): FilterFn<T> => {
  return (row, _, value: { from: Date; to: Date }) => {
    const rowDate = row.getValue(columnId as string) as Date;
    const { from, to } = value;

    if (from && to) {
      return rowDate >= from && rowDate <= to;
    }

    return rowDate >= from;
  };
};

export function convertTemperature(
  value: Temperature,
  toUnit: TemperatureUnit
): Temperature {
  if (value.temperatureUnit === toUnit) {
    return value; // No conversion needed
  }

  if (
    value.temperatureUnit === TemperatureUnit.CELSIUS &&
    toUnit === TemperatureUnit.FAHRENHEIT
  ) {
    return {
      ...value,
      temperature: (value.temperature * 9) / 5 + 32,
      temperatureUnit: TemperatureUnit.FAHRENHEIT,
    }; // Celsius to Fahrenheit
  }

  if (
    value.temperatureUnit === TemperatureUnit.FAHRENHEIT &&
    toUnit === TemperatureUnit.CELSIUS
  ) {
    return {
      ...value,
      temperature: ((value.temperature - 32) * 5) / 9,
      temperatureUnit: TemperatureUnit.CELSIUS,
    };
  }

  throw new Error("Unsupported temperature unit conversion");
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-UK", {
    style: "currency",
    currency: "GBP",
  }).format(amount);
}

export const verifyToken = async (data: string | undefined) => {
  try {
    if (!data) return null;
    const secretKey = new TextEncoder().encode(process.env.JWT_SECRET || "");
    const { payload } = await jwtVerify(data, secretKey);
    return payload;
  } catch (error) {
    console.error("Token verification failed:", JSON.stringify(error, null, 2));
    return null;
  }
};

export const handleApiError = (error: unknown, fallbackMessage: string) => {
  const errorMessage = error instanceof Error ? error.message : fallbackMessage;
  console.error(error);
  toast.error(errorMessage);
};

export function getPhaseStyle(phase: WastagePhase): string {
  switch (phase) {
    case WastagePhase.PORTIONING:
      return "bg-gray-100 text-gray-800";
    case WastagePhase.PREPARATION:
      return "bg-blue-100 text-blue-800";
    case WastagePhase.SERVING:
      return "bg-green-100 text-green-800";
    case WastagePhase.STORAGE:
      return "bg-orange-100 text-orange-800";
    default:
      return "";
  }
}

export const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15MB in bytes
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/jpg"];

export const validateImage = (file: File): string | null => {
  if (!ALLOWED_TYPES.includes(file.type)) {
    return "File type not supported. Please upload JPEG, PNG, or WebP images.";
  }
  if (file.size > MAX_FILE_SIZE) {
    return "File size exceeds 15MB limit.";
  }
  return null;
};

export const getFullName = (user: UserProfile | null): string => {
  return `${user?.firstName} ${user?.lastName}` || "User";
};

export const getInitials = (user: UserProfile | null): string => {
  return user ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}` : "U";
};

interface DateRange {
  start: string;
  end: string;
}

export const isSameDay = (
  start: DateRange["start"],
  end: DateRange["end"]
): boolean => start.slice(0, 10) === end.slice(0, 10);

export const getSubdomain = (hostname: string): string => {
  const parts = hostname.split(".");

  // e.g., www.sheraton.hofwams.com -> ["www", "sheraton", "hofwams", "com"]
  if (parts.length > 2) {
    const sub = parts[0];
    return sub.toLowerCase() === "www" ? parts[1] : sub;
  }

  return "";
};


export const constructTenantUrl = (subdomain: string): string => {
  return `https://${subdomain}.on.hofwams.com`;
};